import { type ComponentType, type SVGProps } from 'react';
import {
  AdjustmentsHorizontalIcon,
  ArchiveBoxArrowDownIcon,
  ArrowUturnLeftIcon,
  ArrowsRightLeftIcon,
  BanknotesIcon,
  BuildingStorefrontIcon,
  ChartBarIcon,
  ClipboardDocumentCheckIcon,
  ClipboardDocumentListIcon,
  CreditCardIcon,
  CubeIcon,
  CurrencyDollarIcon,
  DocumentCurrencyDollarIcon,
  DocumentTextIcon,
  HomeIcon,
  QrCodeIcon,
  QueueListIcon,
  ReceiptPercentIcon,
  ScaleIcon,
  ShoppingCartIcon,
  UserCircleIcon,
  UserGroupIcon,
  UserPlusIcon,
} from '@heroicons/react/24/outline';
import {
  CUSTOMER_PERMISSIONS,
  INVENTORY_PERMISSIONS,
  MONEY_PERMISSIONS,
  PRODUCT_PERMISSIONS,
  PURCHASES_PERMISSIONS,
  REPORT_PERMISSIONS,
  SALES_PERMISSIONS,
  SUPPLIER_PERMISSIONS,
  USER_PERMISSIONS,
  type Permission,
} from '@invento/shared';
import { ROUTE_PATHS } from './constants';

export interface NavRequirement {
  requires?: Permission;
  requiresAny?: readonly Permission[];
  requiresAll?: readonly Permission[];
}

export type NavIcon = ComponentType<SVGProps<SVGSVGElement>>;

export interface NavLinkItem extends NavRequirement {
  type: 'link';
  path: string;
  icon?: NavIcon;
  labelKey: string;
  activeMatch?: (pathname: string) => boolean;
}

export interface NavGroupItem {
  type: 'group';
  id: string;
  collapsedIcon: NavIcon;
  labelKey: string;
  children: NavLinkItem[];
}

export type NavEntry = NavLinkItem | NavGroupItem;

export const navItems: NavEntry[] = [
  {
    type: 'group',
    id: 'workspace',
    collapsedIcon: HomeIcon,
    labelKey: 'navigation:workspace',
    children: [
      { type: 'link', path: ROUTE_PATHS.HOME, icon: HomeIcon, labelKey: 'navigation:dashboard' },
      {
        type: 'link',
        path: ROUTE_PATHS.TEAM.ROOT,
        icon: UserCircleIcon,
        labelKey: 'navigation:team',
        requires: USER_PERMISSIONS.READ,
        activeMatch: (pathname) => pathname === ROUTE_PATHS.TEAM.ROOT || pathname.startsWith('/team/'),
      },
    ],
  },
  {
    type: 'group',
    id: 'inventory',
    collapsedIcon: CubeIcon,
    labelKey: 'navigation:inventory',
    children: [
      {
        type: 'link',
        path: ROUTE_PATHS.INVENTORY.ROOT,
        icon: CubeIcon,
        labelKey: 'navigation:products',
        requiresAny: [PRODUCT_PERMISSIONS.READ, INVENTORY_PERMISSIONS.READ],
        activeMatch: (pathname) =>
          pathname === ROUTE_PATHS.INVENTORY.ROOT || pathname.startsWith('/inventory/products'),
      },
      {
        type: 'link',
        path: ROUTE_PATHS.INVENTORY.RECEIVE,
        icon: ArchiveBoxArrowDownIcon,
        labelKey: 'navigation:receiveStock',
        requires: INVENTORY_PERMISSIONS.RECEIVE,
      },
      {
        type: 'link',
        path: ROUTE_PATHS.INVENTORY.ADJUST,
        icon: AdjustmentsHorizontalIcon,
        labelKey: 'navigation:adjustStock',
        requires: INVENTORY_PERMISSIONS.ADJUST,
      },
      {
        type: 'link',
        path: ROUTE_PATHS.INVENTORY.NEW_TRANSFER,
        icon: ArrowsRightLeftIcon,
        labelKey: 'navigation:transferStock',
        requiresAll: [
          INVENTORY_PERMISSIONS.TRANSFER_CREATE,
          INVENTORY_PERMISSIONS.STOCK_CROSS_BRANCH_TRANSFER,
        ],
        activeMatch: (pathname) => pathname.startsWith('/inventory/transfers'),
      },
    ],
  },
  {
    type: 'group',
    id: 'purchases',
    collapsedIcon: ClipboardDocumentListIcon,
    labelKey: 'navigation:purchases',
    children: [
      {
        type: 'link',
        path: ROUTE_PATHS.PURCHASES.DOCUMENTS,
        icon: ClipboardDocumentListIcon,
        labelKey: 'navigation:purchaseDocuments',
        requires: PURCHASES_PERMISSIONS.READ,
        activeMatch: (pathname) =>
          pathname === ROUTE_PATHS.PURCHASES.ROOT || pathname.startsWith('/purchases/documents'),
      },
      {
        type: 'link',
        path: ROUTE_PATHS.PURCHASES.SUPPLIERS,
        icon: BuildingStorefrontIcon,
        labelKey: 'navigation:suppliers',
        requires: SUPPLIER_PERMISSIONS.READ,
        activeMatch: (pathname) => pathname.startsWith('/purchases/suppliers'),
      },
      {
        type: 'link',
        path: ROUTE_PATHS.PURCHASES.NEW_PAYMENT,
        icon: BanknotesIcon,
        labelKey: 'navigation:paySupplier',
        requiresAll: [
          PURCHASES_PERMISSIONS.PAYMENT_CREATE,
          MONEY_PERMISSIONS.ACCOUNT_READ,
        ],
      },
      {
        type: 'link',
        path: ROUTE_PATHS.PURCHASES.RETURNS,
        icon: ArrowUturnLeftIcon,
        labelKey: 'navigation:supplierReturns',
        requires: PURCHASES_PERMISSIONS.RETURN_READ,
        activeMatch: (pathname) => pathname.startsWith('/purchases/returns'),
      },
    ],
  },
  {
    type: 'group',
    id: 'money',
    collapsedIcon: CreditCardIcon,
    labelKey: 'navigation:money',
    children: [
      {
        type: 'link',
        path: ROUTE_PATHS.MONEY.ROOT,
        icon: CreditCardIcon,
        labelKey: 'navigation:moneyAccounts',
        requires: MONEY_PERMISSIONS.ACCOUNT_READ,
        activeMatch: (pathname) => pathname === ROUTE_PATHS.MONEY.ROOT,
      },
      {
        type: 'link',
        path: ROUTE_PATHS.MONEY.TRANSACTIONS,
        icon: QueueListIcon,
        labelKey: 'navigation:moneyTransactions',
        requires: MONEY_PERMISSIONS.TRANSACTION_READ,
      },
      {
        type: 'link',
        path: ROUTE_PATHS.MONEY.RECONCILIATION,
        icon: ScaleIcon,
        labelKey: 'navigation:moneyReconciliation',
        requiresAll: [
          MONEY_PERMISSIONS.ACCOUNT_READ,
          MONEY_PERMISSIONS.RECONCILIATION_READ,
        ],
      },
      {
        type: 'link',
        path: ROUTE_PATHS.MONEY.CUSTOMER_CREDIT,
        icon: CurrencyDollarIcon,
        labelKey: 'navigation:moneyCustomerCredit',
        requires: MONEY_PERMISSIONS.CUSTOMER_CREDIT_READ,
      },
    ],
  },
  {
    type: 'group',
    id: 'analytics',
    collapsedIcon: ChartBarIcon,
    labelKey: 'navigation:analytics',
    children: [
      {
        type: 'link',
        path: ROUTE_PATHS.REPORTS,
        icon: ChartBarIcon,
        labelKey: 'navigation:reports',
        requires: REPORT_PERMISSIONS.READ,
      },
    ],
  },
  {
    type: 'group',
    id: 'sales',
    collapsedIcon: DocumentCurrencyDollarIcon,
    labelKey: 'navigation:sales',
    children: [
      {
        type: 'link',
        path: ROUTE_PATHS.SALES.ROOT,
        icon: DocumentCurrencyDollarIcon,
        labelKey: 'navigation:invoices',
        requires: SALES_PERMISSIONS.READ,
        activeMatch: (pathname) =>
          pathname === ROUTE_PATHS.SALES.ROOT || pathname.startsWith('/sales/invoices'),
      },
      {
        type: 'link',
        path: ROUTE_PATHS.SALES.POS,
        icon: QrCodeIcon,
        labelKey: 'navigation:pos',
        requires: SALES_PERMISSIONS.POS_CREATE,
      },
      {
        type: 'link',
        path: ROUTE_PATHS.SALES.POS_SHIFT_REVIEWS,
        icon: ClipboardDocumentCheckIcon,
        labelKey: 'navigation:posShiftReviews',
        requires: SALES_PERMISSIONS.POS_SHIFT_VARIANCE_APPROVE,
      },
      {
        type: 'link',
        path: ROUTE_PATHS.SALES.QUOTES,
        icon: DocumentTextIcon,
        labelKey: 'navigation:quotes',
        requires: SALES_PERMISSIONS.READ,
      },
      {
        type: 'link',
        path: ROUTE_PATHS.SALES.ORDERS,
        icon: ShoppingCartIcon,
        labelKey: 'navigation:salesOrders',
        requires: SALES_PERMISSIONS.READ,
      },
      {
        type: 'link',
        path: ROUTE_PATHS.SALES.RETURNS,
        icon: ArrowUturnLeftIcon,
        labelKey: 'navigation:returns',
        requires: SALES_PERMISSIONS.RETURN_READ,
      },
      {
        type: 'link',
        path: ROUTE_PATHS.SALES.CREDIT_NOTES,
        icon: ReceiptPercentIcon,
        labelKey: 'navigation:creditNotes',
        requires: SALES_PERMISSIONS.CREDIT_NOTE_READ,
      },
      {
        type: 'link',
        path: ROUTE_PATHS.SALES.NEW_PAYMENT,
        icon: CurrencyDollarIcon,
        labelKey: 'navigation:recordPayment',
        requires: SALES_PERMISSIONS.PAYMENT_CREATE,
      },
      {
        type: 'link',
        path: ROUTE_PATHS.SALES.NEW_STATEMENT,
        icon: DocumentTextIcon,
        labelKey: 'navigation:generateStatement',
        requiresAll: [
          CUSTOMER_PERMISSIONS.STATEMENT_READ,
          SALES_PERMISSIONS.STATEMENT_CREATE,
          SALES_PERMISSIONS.STATEMENT_SEND,
        ],
      },
    ],
  },
  {
    type: 'group',
    id: 'customers',
    collapsedIcon: UserGroupIcon,
    labelKey: 'navigation:customers',
    children: [
      {
        type: 'link',
        path: ROUTE_PATHS.CUSTOMERS.ROOT,
        icon: UserGroupIcon,
        labelKey: 'navigation:customerList',
        requires: CUSTOMER_PERMISSIONS.READ,
        activeMatch: (pathname) =>
          pathname === ROUTE_PATHS.CUSTOMERS.ROOT ||
          (pathname.startsWith('/customers/') && pathname !== ROUTE_PATHS.CUSTOMERS.NEW),
      },
      {
        type: 'link',
        path: ROUTE_PATHS.CUSTOMERS.NEW,
        icon: UserPlusIcon,
        labelKey: 'navigation:newCustomer',
        requires: CUSTOMER_PERMISSIONS.CREATE,
      },
    ],
  },
];

export function isRouteActive(pathname: string, itemPath: string) {
  return itemPath === '/'
    ? pathname === '/'
    : pathname === itemPath || pathname.startsWith(`${itemPath}/`);
}

export function isLinkActive(pathname: string, item: NavLinkItem) {
  return item.activeMatch ? item.activeMatch(pathname) : isRouteActive(pathname, item.path);
}

export function isGroupActive(pathname: string, item: NavGroupItem) {
  return item.children.some((child) => isLinkActive(pathname, child));
}

export function satisfiesRequirement(userPermissions: readonly string[], item: NavRequirement) {
  if (item.requires && !userPermissions.includes(item.requires)) return false;
  if (item.requiresAny && !item.requiresAny.some((permission) => userPermissions.includes(permission))) {
    return false;
  }
  if (item.requiresAll && !item.requiresAll.every((permission) => userPermissions.includes(permission))) {
    return false;
  }
  return true;
}

export function visibleNavigation(userPermissions: readonly string[]) {
  return navItems.flatMap((item): NavEntry[] => {
    if (item.type === 'link') {
      return satisfiesRequirement(userPermissions, item) ? [item] : [];
    }
    const children = item.children.filter((child) => satisfiesRequirement(userPermissions, child));
    return children.length > 0 ? [{ ...item, children }] : [];
  });
}

export function visibleNavigationGroups(userPermissions: readonly string[]) {
  return visibleNavigation(userPermissions).filter((item): item is NavGroupItem => item.type === 'group');
}
